// First-class byte-splice move planner for comma-separated lists.
// Shared by `move-select-item` (via planMoveSelectItem, over structure.selectItems)
// and `reorder-group-by` (via planReorderGroupBy, over structure.groupBy).
// Replaces the old whole-list re-emit `reorder-select-items`
// planner: instead of rewriting the ENTIRE list span (which normalized every
// inter-item separator to one voted style and glued gap comments into neighbour
// item text), `planListMove` emits at most TWO byte edits —
//   (a) a gap-aware DELETE of the moved item plus ONE adjacent separator gap, and
//   (b) an INSERT of the moved item's EXACT source text with ONE synthesized
//       separator (voted via detectListSeparator) at the new junction.
// Every other inter-item byte (alignment whitespace, newlines, comments) is
// preserved verbatim.
//
// Fail-closed guards: item ranges trim trailing WHITESPACE only (trimRange), so
// an item's text can END in an unterminated `--`/`#` line comment. Three
// junctions could silently corrupt the emitted SQL and are each handled:
//   (1) the MOVED item's own text ends in a line comment — splicing it mid-list
//       would comment out whatever follows on the junction line ⇒ bail ([]);
//   (2) to > from: the insert lands at items[to].end — when items[to]'s text
//       ends in a line comment, `sep + moved` would land inside it ⇒ bail ([]);
//   (3) the default PRECEDING-gap delete would remove the newline that
//       terminates a line comment at the end of items[from-1] (leading-comma
//       style) ⇒ delete the clean FOLLOWING gap instead, or bail ([]) when no
//       clean following gap exists.
//
// Offset math (why the two edits never overlap): arrayMove places the moved
// item at position `to`, so the insert lands at items[to].start (to < from) or
// items[to].end (to > from). The delete covers the moved item plus one adjacent
// gap. For to < from the insert offset is <= items[from-1].start, strictly left
// of the delete; for to > from the insert offset is >= items[from+1].start,
// strictly right of it. `assertNonOverlapping` re-checks this before emitSource
// runs its own validator.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Structural Edit Applicator — reorder (§2.7 S12).

import type { PlannedByteEdit, StructuralEdit } from '../types';
import { findTopLevelCommas } from '../structure/scanner';
import { skipContextFor, trySkipNonCode, type SkipContext } from '../classify/tokenSkip';

import type { EditCtx } from './applyStructuralEdit';
import {
  deleteEdit,
  detectListSeparator,
  insertEdit,
  type ListItem,
} from './applyStructuralEdit.shared';

// A gap is "clean" when it is only whitespace + exactly one comma — i.e. it
// carries no comment (or any other stray token). Deleting a clean gap along
// with the moved item preserves every comment elsewhere in the list. (The
// caller's comma-count guard already ensures each gap holds exactly one comma,
// so this reduces to "no comment".)
function isCleanGap(source: string, start: number, end: number): boolean {
  return source.slice(start, end).replace(/\s/g, '') === ',';
}

// True when `text` contains a line comment (`--`, or `#` on MySQL) that runs
// unterminated to the end of `text` (no closing newline). Item ranges trim
// trailing whitespace only, so a trailing line comment rides INSIDE an item's
// text — used by all three junction guards in the module docblock. Strings /
// quoted identifiers / block comments are skipped atomically via
// `trySkipNonCode` so a `--` inside `'a--b'` never false-positives.
function hasUnterminatedLineComment(text: string, ctx: SkipContext): boolean {
  let i = 0;
  const end = text.length;
  while (i < end) {
    const c = text[i];
    const c2 = text[i + 1] ?? '';
    if ((c === '-' && c2 === '-') || (ctx.supportsHashComment && c === '#')) {
      const nl = text.indexOf('\n', i);
      if (nl === -1) return true;
      i = nl + 1;
      continue;
    }
    const skipped = trySkipNonCode(text, i, end, ctx);
    if (skipped > i) { i = skipped; continue; }
    i += 1;
  }
  return false;
}

// Move `items[fromIndex]` to position `toIndex` (arrayMove semantics) as a
// byte-splice. Returns [] (a no-op) on any guard failure so callers never
// commit a misaddressed edit.
export function planListMove(
  ctx: EditCtx,
  items: readonly ListItem[],
  fromIndex: number,
  toIndex: number,
  rationale: string,
): readonly PlannedByteEdit[] {
  if (
    !Number.isInteger(fromIndex) ||
    !Number.isInteger(toIndex) ||
    items.length < 2 ||
    fromIndex < 0 || fromIndex >= items.length ||
    toIndex < 0 || toIndex >= items.length ||
    fromIndex === toIndex
  ) {
    return [];
  }
  // Every item range must be healthy (non-zero-width). Zero-width ranges (a
  // pre-`trySkipLeadingTrivia` hazard) would misaddress the splice — bail.
  for (const it of items) {
    if (it.range.startOffset >= it.range.endOffset) return [];
  }
  const { source } = ctx;
  const listStart = items[0].range.startOffset;
  const listEnd = items[items.length - 1].range.endOffset;
  // Double-guard: the item count must agree with the actual top-level comma
  // structure of the list span. A mismatch means the item ranges are stale or
  // misaligned (e.g. an expression the extractor split differently than the
  // UI) — bail rather than emit a misaddressed op.
  const commas = findTopLevelCommas(source, listStart, listEnd, ctx.options);
  if (commas.length !== items.length - 1) return [];

  const skipCtx = skipContextFor(ctx.options);
  const moved = source.slice(items[fromIndex].range.startOffset, items[fromIndex].range.endOffset);
  // Guard (1): the moved text ends inside a line comment — splicing it would
  // comment out the tokens that land after it on the junction line (silent
  // column loss / eaten FROM). See the module docblock.
  if (hasUnterminatedLineComment(moved, skipCtx)) return [];
  // Guard (2): for to > from the insert lands at items[to].end — when THAT
  // item's text ends in a line comment, `sep + moved` would land inside it
  // (shape X: the mirror gesture of guard 1).
  if (toIndex > fromIndex && hasUnterminatedLineComment(itemText(source, items, toIndex), skipCtx)) {
    return [];
  }
  const sep = detectListSeparator(source, items);

  const del = planMoveDelete(ctx, items, fromIndex, skipCtx, rationale);
  if (!del) return [];
  const ins = toIndex < fromIndex
    ? insertEdit(ctx, items[toIndex].range.startOffset, moved + sep, rationale)
    : insertEdit(ctx, items[toIndex].range.endOffset, sep + moved, rationale);

  assertNonOverlapping(del, ins);
  return [del, ins];
}

function itemText(source: string, items: readonly ListItem[], index: number): string {
  return source.slice(items[index].range.startOffset, items[index].range.endOffset);
}

// Choose which adjacent gap to delete alongside the moved item. Default =
// PRECEDING gap + item (planListRemove parity); index 0 has no preceding gap
// so it deletes item + FOLLOWING gap. Returns null (⇒ caller bails) when the
// preceding gap is load-bearing (guard 3) and no clean following gap exists.
function planMoveDelete(
  ctx: EditCtx,
  items: readonly ListItem[],
  fromIndex: number,
  skipCtx: SkipContext,
  rationale: string,
): PlannedByteEdit | null {
  const { source } = ctx;
  const last = items.length - 1;
  const hasPreceding = fromIndex > 0;
  const hasFollowing = fromIndex < last;
  const follClean = hasFollowing && isCleanGap(
    source, items[fromIndex].range.endOffset, items[fromIndex + 1].range.startOffset,
  );
  let useFollowing = !hasPreceding;
  // Guard (3), shape Y: when items[from-1]'s text ends in an unterminated line
  // comment, the preceding gap's newline is what TERMINATES that comment —
  // deleting the gap would swallow the junction. Dodge to a clean following
  // gap, or refuse the move when there is none.
  if (hasPreceding && hasUnterminatedLineComment(itemText(source, items, fromIndex - 1), skipCtx)) {
    if (!follClean) return null;
    useFollowing = true;
  }
  if (hasPreceding && hasFollowing && !useFollowing) {
    const precClean = isCleanGap(
      source, items[fromIndex - 1].range.endOffset, items[fromIndex].range.startOffset,
    );
    // Comment-dodging: when the default (preceding) gap carries a comment and
    // the following gap is clean, delete the clean gap instead so the comment
    // survives the move. When BOTH adjacent gaps carry comments the default
    // gap's comment is dropped with the move (documented remove-parity
    // behavior — same as removing that item).
    if (!precClean && follClean) useFollowing = true;
  }
  if (useFollowing) {
    return deleteEdit(
      ctx, items[fromIndex].range.startOffset, items[fromIndex + 1].range.startOffset, rationale,
    );
  }
  return deleteEdit(
    ctx, items[fromIndex - 1].range.endOffset, items[fromIndex].range.endOffset, rationale,
  );
}

// Defense in depth before emitSource's own non-overlap check.
function assertNonOverlapping(a: PlannedByteEdit, b: PlannedByteEdit): void {
  const lo = a.range.startOffset <= b.range.startOffset ? a : b;
  const hi = lo === a ? b : a;
  if (lo.range.endOffset > hi.range.startOffset) {
    throw new Error(
      `planListMove: planned move edits overlap — [${lo.range.startOffset}, ${lo.range.endOffset}) and [${hi.range.startOffset}, ${hi.range.endOffset})`,
    );
  }
}

export function planMoveSelectItem(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'move-select-item' }>,
): readonly PlannedByteEdit[] {
  const items = ctx.structure.selectItems ?? [];
  return planListMove(ctx, items, edit.fromIndex, edit.toIndex, 'move-select-item');
}

// GROUP BY item reorder — the same byte-splice as move-select-item over a
// different comma list. planListMove's comma-count double-guard is load-bearing
// here: expression-shaped group-by items whose extraction disagrees with the
// top-level-comma structure bail to [] rather than commit a misaddressed op.
export function planReorderGroupBy(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'reorder-group-by' }>,
): readonly PlannedByteEdit[] {
  const items = ctx.structure.groupBy ?? [];
  return planListMove(ctx, items, edit.fromIndex, edit.toIndex, 'reorder-group-by');
}
